/** At-a-glance view: today's numbers, quick add, last two weeks, weight trend. */
import type { ReactElement } from 'react';
import { EnergySummary, HighlightRings } from '../components/Panels';
import { QuickAdd } from '../components/QuickAdd';
import { TimeChart } from '../components/TimeChart';
import { Card, Skeleton, StatTile } from '../components/ui';
import { addDays } from '../lib/dates';
import type { DayBurn } from '../lib/energy';
import { sumNutrients } from '../lib/nutrition';
import type { DailyMetric, MealLog } from '../lib/types';
import { emaTrend } from '../lib/trend';
import { massFromKg, massUnit } from '../lib/units';
import { useApp, useAsync, useDayBurn } from '../state/app';

/** "resting + 780 active + 200 walking + digestion". */
function burnParts(burn: DayBurn): string {
  const parts: string[] = ['resting'];
  if (burn.activeKcal !== null) parts.push(`${Math.round(burn.activeKcal).toLocaleString()} active`);
  if (burn.walkingKcal !== null && burn.walkingKcal > 0) parts.push(`${Math.round(burn.walkingKcal).toLocaleString()} walking`);
  if (burn.tefKcal) parts.push('digestion');
  return parts.join(' + ');
}

export function DashboardPage({ go }: { go: (route: string) => void }): ReactElement {
  const { source, profile, day, version, energy, trajectory } = useApp();
  const units = profile.unit_system;
  const meals = useAsync<MealLog[]>(() => source.listMeals(day), [source, day, version]);
  const metrics = useAsync<DailyMetric[]>(() => source.listDailyMetrics(addDays(day, -41), day), [source, day, version]);
  const totals = sumNutrients(meals.data ?? []);
  const { burn } = useDayBurn(day, meals.data ?? []);
  const typical: number | null = energy.planning.kcal;
  const m: DailyMetric[] = metrics.data ?? [];
  const last14: DailyMetric[] = m.filter((d) => d.day > addDays(day, -14) && d.meal_count > 0);
  const weights = m.filter((d) => d.weight_kg !== null).map((d) => ({ day: d.day, y: massFromKg(d.weight_kg as number, units) }));
  const weightTrend = emaTrend(m.filter((d) => d.weight_kg !== null).map((d) => ({ day: d.day, weight: d.weight_kg as number }))).map((p) => ({ day: p.day, y: massFromKg(p.y, units) }));
  const trend = trajectory?.trend;
  const target: number | null = profile.target_calories;

  const weekly: number | null = trend?.weeklyChangeKg ?? null;
  const weeklyText: string | undefined = weekly !== null ? `${weekly > 0 ? '+' : ''}${massFromKg(weekly, units).toFixed(2)} ${massUnit(units)}/week` : undefined;
  const loggedDays: number = last14.length;
  const avg14: number | null = loggedDays > 0 ? last14.reduce((a, d) => a + d.calories, 0) / loggedDays : null;

  return (
    <div className="stack">
      <div className="tiles">
        <StatTile label="Eaten today" value={Math.round(totals.calories).toLocaleString()} unit="kcal" delta={target ? `${Math.round(target - totals.calories).toLocaleString()} left of ${target.toLocaleString()}` : 'Set a calorie target in Profile'} />
        <StatTile label="Protein today" value={Math.round(totals.protein_g).toString()} unit="g" delta={profile.target_protein_g ? `${Math.round((totals.protein_g / profile.target_protein_g) * 100)}% of ${profile.target_protein_g} g` : undefined} deltaClass={profile.target_protein_g && totals.protein_g >= profile.target_protein_g ? 'good' : 'muted'} />
        <StatTile label="Trend weight" value={trend?.smoothedWeightKg ? massFromKg(trend.smoothedWeightKg, units).toFixed(1) : '—'} unit={massUnit(units)} delta={weeklyText} deltaClass={weekly === null ? 'muted' : weekly < 0 ? 'good' : 'warn'} />
        <StatTile label={burn.partial ? 'Burned so far' : 'Burned'} value={burn.kcal !== null ? Math.round(burn.kcal).toLocaleString() : '—'} unit="kcal"
          delta={burn.method === 'wearable' ? (burn.basis === 'active' ? burnParts(burn) : 'watch total + digestion')
            : burn.method === 'observed' ? 'learned from your weight + intake' : burn.method === 'activity' ? `resting × ${burn.pal} activity level` : 'fill in Targets & Profile'} />
      </div>

      <div className="grid cols-main">
        <div className="stack">
          <QuickAdd />
          <Card title="Calories" sub="last 14 days" right={<button type="button" className="btn btn-sm" onClick={() => go('trends')}>Trends →</button>}>
            {metrics.loading && metrics.data === null ? <Skeleton height={220} /> : (
              <TimeChart height={220} zeroBased emptyText="Log a few meals to see this chart"
                bars={{ name: 'Eaten', color: 'var(--energy)', points: last14.map((d) => ({ day: d.day, y: Math.round(d.calories), color: target && d.calories > target * 1.05 ? 'var(--bad)' : 'var(--energy)' })) }}
                refLines={[...(target ? [{ y: target, label: `target ${target}`, color: 'var(--text-2)' }] : []), ...(typical ? [{ y: Math.round(typical), label: `typical burn ${Math.round(typical)}`, color: 'var(--burn)' }] : [])]}
                yFormat={(v) => Math.round(v).toLocaleString()} />
            )}
            {avg14 !== null && <p className="faint" style={{ margin: '8px 0 0', fontSize: 13, fontWeight: 600 }}>Average {Math.round(avg14).toLocaleString()} kcal on {loggedDays} logged {loggedDays === 1 ? 'day' : 'days'}.</p>}
          </Card>
          <Card title="Weight" sub="last 6 weeks" right={<button type="button" className="btn btn-sm" onClick={() => go('body')}>Body →</button>}>
            <TimeChart height={200} emptyText="Log a weigh-in to start your trend"
              lines={[{ name: 'Weigh-in', color: 'var(--text-3)', points: weights, dots: true, hideLine: true }, { name: 'Trend', color: 'var(--protein)', points: weightTrend, width: 3 }]}
              yFormat={(v) => v.toFixed(0)} />
          </Card>
        </div>
        <div className="stack">
          <EnergySummary totals={totals} profile={profile} burn={burn} />
          <HighlightRings totals={totals} profile={profile} />
        </div>
      </div>
    </div>
  );
}
